import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const LAST_PRODUCT_STORAGE_KEY = 'theme:cart-cross-sell:last-product-id';

/**
 * Build the recommendations URL while preserving Shopify preview parameters
 * from the current page URL.
 * @returns {URL}
 */
function buildRecommendationsURL() {
  const currentUrl = new URL(window.location.href);
  const recommendationsUrl = new URL(Theme.routes.product_recommendations_url, currentUrl);

  for (const [key, value] of currentUrl.searchParams.entries()) {
    if (!recommendationsUrl.searchParams.has(key)) {
      recommendationsUrl.searchParams.set(key, value);
    }
  }

  return recommendationsUrl;
}

class CartCrossSellComponent extends Component {
  static observedAttributes = ['data-url'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #drawer = null;

  /** @type {string | null} */
  #loadedProductId = null;

  /** @type {string | null} */
  #cachedContent = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.source !== 'recommendations') return;

    this.#drawer = this.closest('cart-drawer-component');
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.#drawer?.addEventListener('dialog:open', this.#handleDrawerOpen);

    const productId = this.#resolveProductId();
    if (productId && !this.dataset.url) {
      this.dataset.url = this.#buildRecommendationURL(productId);
    }

    if (this.#isDrawerOpen()) {
      const url = this.dataset.url;
      if (url) {
        this.load(url);
      }
    }
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.dataset.source !== 'recommendations') return;

    const url = this.dataset.url;
    if (!url || !this.#isDrawerOpen()) return;

    const productId = this.#getRequestProductId(url);
    if (productId && this.dataset.renderState === 'loading' && this.#restoreCachedContent(productId)) {
      return;
    }

    if (this.dataset.renderState === 'loading') {
      this.load(url);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.#drawer?.removeEventListener('dialog:open', this.#handleDrawerOpen);
    this.#abortController?.abort();
    this.#abortController = null;
    this.#drawer = null;
  }

  #handleDrawerOpen = () => {
    const url = this.dataset.url;
    if (url) {
      this.load(url);
      return;
    }

    const productId = this.#resolveProductId();
    if (productId) {
      this.dataset.url = this.#buildRecommendationURL(productId);
    }
  };

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-url' && newValue && newValue !== oldValue && this.#isDrawerOpen()) {
      this.load(newValue);
    }
  }

  /**
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) return;

    const itemCount = event.detail?.data?.itemCount;
    if (itemCount === 0) {
      this.#clearStoredProductId();
      return;
    }

    if (event.detail?.data?.source === 'gwp-offer') return;

    const productId = event.detail?.data?.productId;
    if (!productId) return;

    const normalizedProductId = String(productId);

    this.#setStoredProductId(normalizedProductId);
    this.dataset.referenceProductId = normalizedProductId;
    this.dataset.url = this.#buildRecommendationURL(normalizedProductId);
  };

  /**
   * @param {string} url
   */
  async load(url) {
    const requestUrl = new URL(url, buildRecommendationsURL());
    const productId = requestUrl.searchParams.get('product_id');

    if (!productId) {
      this.remove();
      return;
    }

    if ((this.dataset.loadedProductId === productId || this.#loadedProductId === productId) && this.dataset.renderState === 'ready') {
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.dataset.referenceProductId = productId;
    this.setAttribute('aria-busy', 'true');

    try {
      let source = await this.#fetchSource(requestUrl.toString());

      if (!(source instanceof HTMLElement)) {
        const fallbackUrl = this.#buildFallbackURL(requestUrl);

        if (fallbackUrl) {
          source = await this.#fetchSource(fallbackUrl);

          if (source instanceof HTMLElement) {
            this.dataset.activeIntent = new URL(fallbackUrl).searchParams.get('intent') || 'related';
          }
        }
      } else {
        this.dataset.activeIntent = requestUrl.searchParams.get('intent') || this.dataset.intent || '';
      }

      if (!(source instanceof HTMLElement)) {
        this.remove();
        return;
      }

      this.replaceChildren(...source.childNodes);
      this.#loadedProductId = productId;
      this.dataset.loadedProductId = productId;
      this.dataset.renderState = 'ready';
      this.classList.remove('cart-cross-sell--loading');
      this.#cachedContent = this.innerHTML;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      this.remove();
    } finally {
      if (this.isConnected) {
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchSource(url) {
    const response = await fetch(url, { signal: this.#abortController?.signal });

    if (!response.ok) {
      return null;
    }

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = html.querySelector('cart-cross-sell-component');

    return source instanceof HTMLElement ? source : null;
  }

  /**
   * @param {string} productId
   */
  #buildRecommendationURL(productId) {
    const url = buildRecommendationsURL();
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', this.dataset.productLimit || '6');
    url.searchParams.set('intent', this.dataset.intent || 'complementary');
    return url.toString();
  }

  /**
   * @param {URL} url
   */
  #buildFallbackURL(url) {
    const currentIntent = url.searchParams.get('intent') || this.dataset.intent || 'complementary';

    if (currentIntent === 'related') {
      return null;
    }

    const fallbackUrl = new URL(url);
    fallbackUrl.searchParams.set('intent', 'related');
    return fallbackUrl.toString();
  }

  /**
   * @param {string} url
   */
  #getRequestProductId(url) {
    try {
      return new URL(url, buildRecommendationsURL()).searchParams.get('product_id');
    } catch {
      return null;
    }
  }

  /**
   * @param {string} productId
   */
  #restoreCachedContent(productId) {
    if (!this.#cachedContent || this.#loadedProductId !== productId) {
      return false;
    }

    this.innerHTML = this.#cachedContent;
    this.dataset.loadedProductId = productId;
    this.dataset.renderState = 'ready';
    this.classList.remove('cart-cross-sell--loading');
    return true;
  }

  #resolveProductId() {
    return this.#getStoredProductId() || this.dataset.referenceProductId || null;
  }

  #isDrawerOpen() {
    const dialog = this.closest('dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  #getStoredProductId() {
    try {
      return sessionStorage.getItem(LAST_PRODUCT_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} productId
   */
  #setStoredProductId(productId) {
    try {
      sessionStorage.setItem(LAST_PRODUCT_STORAGE_KEY, productId);
    } catch {
      // Ignore storage failures and keep the component functional.
    }
  }

  #clearStoredProductId() {
    try {
      sessionStorage.removeItem(LAST_PRODUCT_STORAGE_KEY);
    } catch {
      // Ignore storage failures and keep the component functional.
    }
  }
}

if (!customElements.get('cart-cross-sell-component')) {
  customElements.define('cart-cross-sell-component', CartCrossSellComponent);
}